"use client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Alert,
  Avatar,
  Checkbox,
  FormControlLabel,
  alpha,
  useTheme,
} from "@mui/material";
import {
  VisibilityTwoTone,
  VisibilityOffTwoTone,
  FavoriteBorderTwoTone,
} from "@mui/icons-material";
import ForgotPasswordDialog from "./forgotpassword";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";

function LoginForm({ onToggleMode }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [generalError, setGeneralError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const theme = useTheme();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    setPasswordError("");
    setGeneralError("");

    let hasError = false;

    if (!email) {
      setEmailError("Email is required");
      hasError = true;
    }
    if (!password) {
      setPasswordError("Password is required");
      hasError = true;
    }

    if (hasError) return;

    setIsLoading(true);
    try {
      const res = await login(email, password);
      // login() function in AuthContext already handles parsing,
      // but we should ensure the catch block handles any issues.
      if (res) {
        showToast("Login Successfully");
        navigate("/home");
      }
    } catch (err) {
      console.error("Login Error:", err);
      const message = err.message || "Login failed. Try again.";
      if (message === "User not found") setEmailError(message);
      else if (message === "Invalid password") setPasswordError(message);
      else setGeneralError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },

          width: {
            xs: "90%",
            sm: "85%",
            md: "80%",
            lg: "100%",
            xl: "100%",
          },

          maxWidth: { xs: 400, sm: 500, md: 800, lg: 1000, xl: 1200 },

          height: {
            xs: "auto",
            sm: "auto",
            md: 450,
            lg: 550,
            xl: 550,
          },

          minHeight: { xs: 500, sm: 550 },

          borderRadius: 4,
          overflow: "hidden",

          mx: "auto",
          my: "auto",

          bgcolor: "background.paper",

          boxShadow: theme.shadows[2], // optional container shadow
        }}
      >
        {/* Left - Form */}
        <CardContent
          sx={{
            flex: 1,
            p: { xs: 3, sm: 4, md: 4, lg: 5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minWidth: { md: 350, lg: 400 },
          }}
        >
          <Box textAlign="center" mb={2}>
            <Avatar
              sx={{
                width: 60,
                height: 60,
                mx: "auto",
                mb: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: "primary.main",
              }}
            >
              <FavoriteBorderTwoTone fontSize="large" />
            </Avatar>
            <Typography
              variant="h5"
              fontWeight={700}
              color="primary.main"
              gutterBottom
            >
              Welcome Back
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Sign in to continue your journey
            </Typography>
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2.5, sm: 3 },
            }}
          >
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              error={!!emailError}
              helperText={emailError}
              size="medium"
            />
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              error={!!passwordError}
              helperText={passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? (
                        <VisibilityOffTwoTone />
                      ) : (
                        <VisibilityTwoTone />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <FormControlLabel
                control={<Checkbox size="small" color="secondary" />} // Gold checkbox
                label={
                  <Typography variant="body2" color="text.secondary">
                    Remember me
                  </Typography>
                }
              />
              <Button
                variant="text"
                onClick={() => setForgotOpen(true)}
                sx={{
                  p: 0,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "primary.main",
                  // "&:hover": {
                  //   textDecoration: "underline",
                  //   bgcolor: "transparent",
                  // },
                }}
              >
                Forgot password?
              </Button>
            </Box>

            {generalError && (
              <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                {generalError}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              color="primary" // Use theme primary (Maroon)
              disabled={isLoading}
              sx={{
                mt: 1,
                py: 1.5,
                fontSize: "1rem",
              }}
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </Button>
          </Box>

          {/* Sign up link */}
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              Don't have an account?{" "}
              <Button
                variant="text"
                onClick={onToggleMode}
                sx={{
                  p: 0,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "secondary.main", // Gold
                  minWidth: "auto",
                  "&:hover": {
                    bgcolor: "transparent",
                  },
                }}
              >
                Sign up here
              </Button>
            </Typography>
          </Box>
        </CardContent>

        {/* Right - Image */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            bgcolor: "background.default",
          }}
        >
          <Box
            component="img"
            src="chimma.jpg"
            alt="Wedding rings"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: 3,
              boxShadow: theme.shadows[4],
            }}
          />
        </Box>
      </Card>

      <ForgotPasswordDialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        initialEmail={email}
      />
    </>
  );
}

export default LoginForm;
